import { REDIRECT_URI } from './config'
import type { PkceState } from './types'

const PKCE_STORAGE_PREFIX = 'spectreiq_pkce_'
const PKCE_MAX_AGE_MS = 10 * 60 * 1000

function base64URLEncode(buffer: Uint8Array): string {
  const base64 = btoa(String.fromCharCode(...buffer))
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const randomBytes = crypto.getRandomValues(new Uint8Array(32))
  const codeVerifier = base64URLEncode(randomBytes)

  const encoder = new TextEncoder()
  const data = encoder.encode(codeVerifier)
  const hash = await crypto.subtle.digest('SHA-256', data)
  const codeChallenge = base64URLEncode(new Uint8Array(hash))

  return { codeVerifier, codeChallenge }
}

export function storePKCEState(
  state: string,
  codeVerifier: string,
  nonce: string,
  realm: string,
  tenantCode: string | null
): boolean {
  const key = `${PKCE_STORAGE_PREFIX}${state}`
  const value = JSON.stringify({
    codeVerifier,
    nonce,
    realm,
    tenantCode,
    timestamp: Date.now(),
    redirectUri: REDIRECT_URI,
  })

  try {
    sessionStorage.setItem(key, value)
    if (sessionStorage.getItem(key) === value) {
      console.log('[Auth] PKCE state stored in sessionStorage', { state: `${state.substring(0, 8)}...` })
      return true
    }
  } catch (error) {
    console.warn('[Auth] sessionStorage write failed', error)
  }

  try {
    localStorage.setItem(key, value)
    if (localStorage.getItem(key) === value) {
      console.log('[Auth] PKCE state stored in localStorage (fallback)', { state: `${state.substring(0, 8)}...` })
      return true
    }
  } catch (error) {
    console.error('[Auth] localStorage write also failed', error)
  }

  return false
}

export function retrievePKCEState(state: string): PkceState | null {
  const key = `${PKCE_STORAGE_PREFIX}${state}`

  let value = sessionStorage.getItem(key)
  let source = 'sessionStorage'
  if (!value) {
    value = localStorage.getItem(key)
    source = 'localStorage'
  }

  if (!value) {
    console.error('[Auth] PKCE state not found for state', { state: `${state.substring(0, 8)}...` })
    return null
  }

  sessionStorage.removeItem(key)
  localStorage.removeItem(key)

  try {
    const parsed = JSON.parse(value)
    if (Date.now() - parsed.timestamp > PKCE_MAX_AGE_MS) {
      console.warn('[Auth] PKCE state is stale (>10 min old)', { age: Date.now() - parsed.timestamp })
      return null
    }
    console.log('[Auth] PKCE state retrieved from', source, { state: `${state.substring(0, 8)}...` })
    return {
      codeVerifier: parsed.codeVerifier,
      nonce: parsed.nonce,
      realm: parsed.realm,
      tenantCode: parsed.tenantCode ?? null,
    }
  } catch (error) {
    console.error('[Auth] Failed to parse PKCE state', error)
    return null
  }
}

export function cleanupStalePKCEStates(): void {
  const now = Date.now()

  for (const storage of [sessionStorage, localStorage]) {
    const keysToRemove: string[] = []
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i)
      if (!key?.startsWith(PKCE_STORAGE_PREFIX)) {
        continue
      }

      try {
        const value = storage.getItem(key)
        if (!value) {
          continue
        }
        const parsed = JSON.parse(value)
        if (now - parsed.timestamp > PKCE_MAX_AGE_MS) {
          keysToRemove.push(key)
        }
      } catch {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach(key => storage.removeItem(key))
    if (keysToRemove.length > 0) {
      console.log('[Auth] Cleaned up stale PKCE states', { count: keysToRemove.length })
    }
  }
}
